import { RefObject, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

/**
 * Sends a scrolling element back to the top whenever the route changes.
 *
 * The layouts that own these elements stay mounted while only their `<Outlet />`
 * swaps, so nothing resets the scroll on its own and a page opens wherever the
 * previous one was left. React Router's own `ScrollRestoration` is no help
 * here: it needs a data router, and it manages the window scroll rather than a
 * container's.
 *
 * Keyed on the pathname alone — changing a query parameter is a filter or a
 * selected panel, not a new page.
 */
export function useScrollToTopOnNavigate<
    T extends HTMLElement
>(): RefObject<T | null> {
    const ref = useRef<T>(null);
    const { pathname } = useLocation();

    useEffect(() => {
        ref.current?.scrollTo({ top: 0 });
    }, [pathname]);

    return ref;
}
